import React from 'react';
import { Typography, Input, Button, Upload, Radio } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAuthContext } from '../../../context/auth';
import { useUploadMedia } from '../../../hooks/media/use-upload-media';
import { CustomTooltip } from './custom-tooltip';

const { Text } = Typography;

interface VideoFieldProps {
  field: any;
  value: any;
  onChange: (value: any) => void;
  commonStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  error?: string;
}

export const VideoField: React.FC<VideoFieldProps> = ({ field, value, onChange, commonStyle, labelStyle, error }) => {
  const [videoInputType, setVideoInputType] = React.useState<'link' | 'upload'>('link');
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadedFileName, setUploadedFileName] = React.useState<string | null>(null);

  const { userInfo } = useAuthContext();
  const { uploadVideoDirect } = useUploadMedia();

  const clientId = userInfo?.['custom:clientId'];

  const handleUrlChange = (url: string) => {
    onChange({ originalUrl: url });
  };

  const handleUpload = async (file: File) => {
    if (!clientId) {
      console.error('No clientId available');
      return;
    }

    setIsUploading(true);

    try {
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      const result = await uploadVideoDirect('video', fileExtension, file, clientId);

      if (result?.publicUrl) {
        onChange(result.publicUrl);
        setUploadedFileName(file.name);
      }
    } catch (error) {
      console.error('Error uploading video:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      <div style={{ marginBottom: 12 }}>
        <Radio.Group
          value={videoInputType}
          onChange={e => {
            setVideoInputType(e.target.value);
            if (e.target.value === 'link') {
              setUploadedFileName(null);
            }
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <Radio value='link' style={{ fontSize: commonStyle.fontSize }}>
            URL del video
          </Radio>
          <Radio value='upload' style={{ fontSize: commonStyle.fontSize }}>
            Subir archivo
          </Radio>
        </Radio.Group>
      </div>

      {videoInputType === 'link' ? (
        <CustomTooltip field={field}>
          <Input
            placeholder='https://cdn.filestackcontent.com/ejemplo.mp4'
            style={{ fontSize: 12 }}
            value={value?.originalUrl || ''}
            onChange={e => handleUrlChange(e.target.value)}
            status={error ? 'error' : undefined}
          />
        </CustomTooltip>
      ) : (
        <CustomTooltip field={field}>
          <Upload
            accept='video/mp4'
            beforeUpload={file => {
              handleUpload(file);
              return false;
            }}
            showUploadList={false}
            disabled={isUploading}
          >
            <Button
              type='primary'
              icon={<UploadOutlined />}
              loading={isUploading}
              disabled={isUploading}
              style={{ height: '30px' }}
            >
              {isUploading
                ? 'Subiendo...'
                : uploadedFileName
                  ? uploadedFileName.length > 8
                    ? `${uploadedFileName.substring(0, 20)}...`
                    : uploadedFileName
                  : 'Seleccionar video MP4'}
            </Button>
          </Upload>
        </CustomTooltip>
      )}

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};
